/**
 * Grouping/search helpers for the placeholder palette. Placeholders are bucketed by type in the fixed
 * {@link TYPE_ORDER} — one group per type (Transport included; its kinds are shown as per-row icons/chips,
 * not as separate groups). Each group carries its accent color and type.
 */
import type { TFunction } from 'i18next';

import type { PlaceholderListItem } from '@/api/generated/schemas';
import type { PlaceholderType, TransportKind } from '@/api/generated/schemas';
import { TYPE_COLOR, TYPE_ORDER } from '../model';

export interface PaletteGroup {
  key: string;
  label: string;
  color: string;
  type: PlaceholderType;
  transportKind: TransportKind | null;
  items: PlaceholderListItem[];
}

/** Best display name for a placeholder list item. */
export function itemName(item: PlaceholderListItem): string {
  return item.displayName ?? item.name ?? '';
}

/**
 * Small parameter chips shown on a palette row — accommodation category (hvězdičky), facility sub-type
 * (zařízení) and room patterns (pokoje). Transport kind is omitted here because it already labels the group.
 */
export function paramChips(item: PlaceholderListItem, t: TFunction): string[] {
  const chips: string[] = [];
  if (item.accommodationCategoryName) chips.push(item.accommodationCategoryName);
  if (item.facilitySubTypeName) chips.push(item.facilitySubTypeName);
  // `?? []` guards against a backend that predates the list-DTO extension (field absent at runtime).
  const unitPatterns = item.unitPatterns ?? [];
  if (unitPatterns.length > 0) {
    chips.push(unitPatterns.map((pattern) => t(`costScheme.unitPatterns.${pattern}`)).join(' / '));
  }
  return chips;
}

/** Lower-cased searchable text for a placeholder (name + subcategory + destination + params + code). */
export function haystack(item: PlaceholderListItem): string {
  return [
    item.displayName,
    item.name,
    item.subcategoryName,
    item.destinationName,
    item.facilitySubTypeName,
    item.accommodationCategoryName,
    item.code,
    ...(item.unitPatterns ?? []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function typeLabel(t: TFunction, type: PlaceholderType): string {
  return t(`costScheme.types.${type}`);
}

/**
 * Bucket placeholders into ordered palette groups — one group per type in {@link TYPE_ORDER}
 * (Transport not split by kind). Every type always yields a group, even with no matching items, so
 * its blank "drag to create" box stays available before any placeholder exists. Callers drop empty
 * groups themselves when that's desired (e.g. while searching).
 */
export function buildGroups(items: PlaceholderListItem[], t: TFunction): PaletteGroup[] {
  return TYPE_ORDER.map((type) => ({
    key: type,
    label: typeLabel(t, type),
    color: TYPE_COLOR[type],
    type,
    transportKind: null,
    items: items.filter((item) => item.type === type),
  }));
}
